import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ShieldCheck, MailCheck, KeyRound, ArrowLeft, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// dedicated client: no cookies, no auth — works for public flows too
const otpApi = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: false });

const DOC_LABELS = {
  figc_cartellino: "Cartellino FIGC",
  medical_request: "Richiesta visita medica",
  enrollment: "Iscrizione società",
  privacy_release: "Liberatoria privacy e diritto d'immagine",
};

/**
 * Reusable OTP digital-signature dialog.
 *
 * Props:
 *  - open, onOpenChange
 *  - documentType: 'figc_cartellino' | 'medical_request' | 'enrollment' | 'privacy_release'
 *  - documentRef: id of player/medical/enrollment token/etc
 *  - teamId (optional)
 *  - defaultEmail, defaultName
 *  - signerRoleLabel: free text shown to user, e.g. "Genitore" / "Allenatore"
 *  - onSigned: (signature) => void  // called when verification succeeds
 *  - title, description: override
 */
export default function OTPSignatureDialog({
  open,
  onOpenChange,
  documentType,
  documentRef,
  teamId,
  defaultEmail = "",
  defaultName = "",
  signerRoleLabel = "Firmatario",
  onSigned,
  title,
  description,
}) {
  const [step, setStep] = useState("request"); // request | code | done
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [code, setCode] = useState("");
  const [otpId, setOtpId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep("request"); // eslint-disable-line react-hooks/set-state-in-effect
    setEmail(defaultEmail || ""); // eslint-disable-line react-hooks/set-state-in-effect
    setName(defaultName || ""); // eslint-disable-line react-hooks/set-state-in-effect
    setCode(""); // eslint-disable-line react-hooks/set-state-in-effect
    setOtpId(null); // eslint-disable-line react-hooks/set-state-in-effect
    setError(null); // eslint-disable-line react-hooks/set-state-in-effect
    setCooldown(0); // eslint-disable-line react-hooks/set-state-in-effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = useCallback(async () => {
    setError(null);
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError("Inserisci un'email valida");
      return;
    }
    setLoading(true);
    try {
      const { data } = await otpApi.post("/otp/request", {
        email: email.trim().toLowerCase(),
        document_type: documentType,
        document_ref: documentRef,
        team_id: teamId,
        signer_name: name.trim() || null,
        signer_role: signerRoleLabel,
      });
      setOtpId(data.otp_id);
      setStep("code");
      setCooldown(30);
      setTimeout(() => codeInputRef.current?.focus(), 60);
      toast.success(`Codice inviato a ${email}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore invio email");
    }
    setLoading(false);
  }, [email, documentType, documentRef, teamId, name, signerRoleLabel]);

  const verifyCode = useCallback(async () => {
    setError(null);
    if (!code || code.length < 6) {
      setError("Inserisci il codice a 6 cifre");
      return;
    }
    setLoading(true);
    try {
      const { data } = await otpApi.post("/otp/verify", {
        otp_id: otpId,
        code: code.trim(),
      });
      setStep("done");
      toast.success("Firma digitale registrata");
      onSigned?.(data);
      // auto close after a moment
      setTimeout(() => onOpenChange?.(false), 1400);
    } catch (e) {
      setError(e?.response?.data?.detail || "Codice non valido");
    }
    setLoading(false);
  }, [code, otpId, onSigned, onOpenChange]);

  const resendCode = async () => {
    if (cooldown > 0) return;
    await requestCode();
  };

  const headerTitle = title || `Firma digitale · ${DOC_LABELS[documentType] || documentType}`;
  const headerDesc =
    description ||
    "Per ragioni di tracciabilità riceverai un codice via email da inserire qui sotto. La firma verrà registrata con timestamp.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="otp-signature-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-sky-600">
            <ShieldCheck size={18} />
            <span className="text-xs font-semibold uppercase tracking-wider">Firma digitale OTP</span>
          </div>
          <DialogTitle className="font-heading">{headerTitle}</DialogTitle>
          <DialogDescription className="text-sm">{headerDesc}</DialogDescription>
        </DialogHeader>

        {step === "request" && (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="otp-name">Nome e cognome del firmatario</Label>
              <Input
                id="otp-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Es. Mario Rossi"
                data-testid="otp-name-input"
              />
            </div>
            <div>
              <Label htmlFor="otp-email">Email per ricevere il codice *</Label>
              <Input
                id="otp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="mario.rossi@email.it"
                data-testid="otp-email-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Userai questo indirizzo come prova della tua identità per la firma del documento.
              </p>
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>
        )}

        {step === "code" && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-sky-50 border border-sky-200 text-sm">
              <MailCheck size={20} className="text-sky-600 shrink-0" />
              <div>
                <div className="font-medium text-sky-900">Codice inviato</div>
                <div className="text-sky-700 text-xs">Controlla la casella di <strong>{email}</strong> (e lo spam).</div>
              </div>
            </div>
            <div>
              <Label htmlFor="otp-code">Codice a 6 cifre *</Label>
              <Input
                id="otp-code"
                ref={codeInputRef}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                data-testid="otp-code-input"
              />
              <div className="flex items-center justify-between mt-2 text-xs">
                <button
                  type="button"
                  onClick={() => setStep("request")}
                  className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                >
                  <ArrowLeft size={12} /> Modifica email
                </button>
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={cooldown > 0 || loading}
                  className="text-sky-600 hover:text-sky-800 disabled:text-slate-400 inline-flex items-center gap-1"
                  data-testid="otp-resend-button"
                >
                  <KeyRound size={12} /> {cooldown > 0 ? `Reinvia tra ${cooldown}s` : "Reinvia codice"}
                </button>
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>
        )}

        {step === "done" && (
          <div className="py-6 text-center space-y-3">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100">
              <CheckCircle2 size={36} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold">Firma registrata</h3>
              <p className="text-sm text-slate-500 mt-1">Il documento è stato firmato digitalmente.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "request" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={loading}>
                Annulla
              </Button>
              <Button onClick={requestCode} disabled={loading} data-testid="otp-send-button">
                {loading && <Loader2 size={14} className="mr-2 animate-spin" />}
                Invia codice
              </Button>
            </>
          )}
          {step === "code" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={loading}>
                Annulla
              </Button>
              <Button onClick={verifyCode} disabled={loading || code.length < 6} data-testid="otp-verify-button">
                {loading && <Loader2 size={14} className="mr-2 animate-spin" />}
                Verifica e firma
              </Button>
            </>
          )}
          {step === "done" && (
            <Button onClick={() => onOpenChange?.(false)} className="w-full">Chiudi</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
